import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';
import {
  Plus,
  Settings2,
  MoreVertical,
  Edit,
  Trash2,
  GripVertical,
  X,
  ChevronLeft,
  Save,
  Loader2,
  FileSpreadsheet,
} from 'lucide-react';
import { ImportExportManager } from './ImportExportManager';
import { BusinessSelectionModal, CONVERSION_BADGE, PARTIAL_BADGE } from './BusinessSelectionModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Static color maps (full strings required so Tailwind doesn't purge dark: variants)
const STATUS_COLORS = {
  nuevo:          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  contactado:     'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  en_negociacion: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  ganado:         'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  perdido:        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};
const PERIODICITY_COLOR    = 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
const PRESENTATION_YES     = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
const PRESENTATION_NO      = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';

// Columnas por defecto - ACTUALIZADO con Plazo y Estado
const defaultColumns = [
  { id: 'client_name', label: 'Nombre del Cliente', visible: true },
  { id: 'occupation', label: 'Ocupación', visible: true },
  { id: 'business_type', label: 'Negocio', visible: true },
  { id: 'advisor_name', label: 'Asesor', visible: true },
  { id: 'amount', label: 'Monto', visible: true },
  { id: 'periodicity', label: 'Periodicidad', visible: true },
  { id: 'term', label: 'Plazo', visible: true },
  { id: 'next_contact_date', label: 'Fecha de próximo contacto', visible: true },
  { id: 'status', label: 'Estado', visible: true },
  { id: 'presentation', label: 'Presentación', visible: true },
  { id: 'comments', label: 'Comentarios', visible: true },
  { id: 'report_month', label: 'Mes Reporte', visible: true },
  { id: 'actions', label: 'Acciones', visible: true }
];

// Opciones de periodicidad
const periodicityOptions = ['Mensual', 'Trimestral', 'Semestral', 'Anual'];

// Opciones de estado - para Pipeline y conversión a cliente
const statusOptions = [
  { value: 'nuevo', label: 'Nuevo' },
  { value: 'contactado', label: 'Contactado' },
  { value: 'en_negociacion', label: 'En Negociación' },
  { value: 'ganado', label: 'Ganado' },
  { value: 'perdido', label: 'Perdido' },
];

// Componente Sortable para drag & drop
const SortableColumnItem = ({ column, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border ${isDragging ? 'border-blue-500 shadow-lg' : 'border-gray-200 dark:border-gray-700'}`}
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className={column.visible ? '' : 'text-muted-foreground'}>{column.label}</span>
      </div>
      <Switch
        checked={column.visible}
        onCheckedChange={() => onToggle(column.id)}
        disabled={column.id === 'actions'}
      />
    </div>
  );
};

// ─── Custom tooltip for long text ────────────────────────────────────────────
function CommentTooltip({ text }) {
  const [show, setShow] = React.useState(false);
  if (!text) return <span className="text-muted-foreground text-sm">-</span>;
  return (
    <div
      className="relative max-w-[200px]"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span className="text-sm text-muted-foreground truncate block cursor-default">{text}</span>
      {show && (
        <div className="absolute z-50 top-full right-0 mt-1 max-w-sm w-max bg-white dark:bg-gray-800 text-foreground text-sm rounded-lg border border-border shadow-lg p-3 pointer-events-none">
          {text}
        </div>
      )}
    </div>
  );
}

export const SalesReport = ({ onBack }) => {
  const { user } = useAuth();
  const [salesReports, setSalesReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [columns, setColumns] = useState(defaultColumns);
  const [activeMonth, setActiveMonth] = useState(null); // null = show most recent
  const [bizSelectOpen, setBizSelectOpen] = useState(false);
  const [bizSelectSale, setBizSelectSale]  = useState(null);
  const [pendingStatus, setPendingStatus]  = useState('');
  
  // Form data - ACTUALIZADO con term y status
  const currentMonthLabel = (() => {
    const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    const d = new Date();
    return `${MESES[d.getMonth()]} ${d.getFullYear()}`;
  })();

  const [formData, setFormData] = useState({
    client_name: '',
    occupation: '',
    business_type: '',
    amount: '',
    periodicity: 'Mensual',
    term: '',
    next_contact_date: '',
    status: 'nuevo',
    presentation: false,
    comments: '',
    report_month: currentMonthLabel,
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Cargar datos del backend
  const fetchSalesReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sales-reports`);
      setSalesReports(response.data);
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      setSalesReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar preferencias de columnas
  const loadColumnPreferences = useCallback(() => {
    const saved = localStorage.getItem(`sales_report_columns_${user?.id}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const merged = defaultColumns.map(dc => {
          const savedCol = parsed.find(p => p.id === dc.id);
          return savedCol || dc;
        });
        const orderedIds = parsed.map(p => p.id);
        merged.sort((a, b) => {
          const aIdx = orderedIds.indexOf(a.id);
          const bIdx = orderedIds.indexOf(b.id);
          if (aIdx === -1) return 1;
          if (bIdx === -1) return -1;
          return aIdx - bIdx;
        });
        setColumns(merged);
      } catch (e) {
        console.error('Error loading column preferences:', e);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSalesReports();
    loadColumnPreferences();
  }, [fetchSalesReports, loadColumnPreferences]);

  // Guardar preferencias de columnas
  const saveColumnPreferences = () => {
    localStorage.setItem(`sales_report_columns_${user?.id}`, JSON.stringify(columns));
    toast.success('Preferencias de columnas guardadas');
    setColumnsDialogOpen(false);
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setColumns((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Toggle column visibility
  const toggleColumnVisibility = (columnId) => {
    setColumns(prev => prev.map(col =>
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  };

  // Manejar cambio de estado - actualizar Pipeline y convertir a cliente si es "ganado"
  const handleStatusChange = async (saleId, newStatus) => {
    const sale = salesReports.find(s => s.id === saleId);
    if (!sale) return;

    // Intercept "ganado" if lead has multiple businesses → show selection modal
    if (newStatus === 'ganado') {
      const businessList = sale.business_type
        ? String(sale.business_type).split(',').map(s => s.trim()).filter(Boolean)
        : [];
      if (businessList.length > 1) {
        setBizSelectSale(sale);
        setPendingStatus(newStatus);
        setBizSelectOpen(true);
        return;
      }
    }

    await _applyStatusChange(sale, newStatus, null, null);
  };

  const _applyStatusChange = async (sale, newStatus, convertedBusinesses, remainingBusinesses) => {
    try {
      // Update sales_report status
      await axios.put(`${API}/sales-reports/${sale.id}`, {
        ...sale,
        status: newStatus
      }).catch(() => {});

      // Sync to lead if linked
      if (sale.lead_id) {
        const leadPayload = { status: newStatus };
        if (convertedBusinesses) leadPayload.converted_business_types = convertedBusinesses;
        if (remainingBusinesses)  leadPayload.remaining_business_types  = remainingBusinesses;
        await axios.put(`${API}/leads/${sale.lead_id}`, leadPayload).catch(() => {});
      }

      // If ganado: convert to client (backend handles it via PUT /leads)
      if (newStatus === 'ganado' && sale.client_name && !sale.lead_id) {
        try {
          await axios.post(`${API}/clients`, {
            name: sale.client_name,
            occupation: sale.occupation,
            notes: `Convertido desde Reporte de Ventas. Negocio: ${sale.business_type}. Monto: ${sale.amount}`,
            source: 'Reporte de Ventas',
            status: 'activo'
          });
          toast.success(`¡${sale.client_name} convertido a cliente!`);
        } catch (err) {
          console.error('Error al convertir a cliente:', err);
        }
      }

      setSalesReports(prev => prev.map(s =>
        s.id === sale.id
          ? { ...s, status: newStatus, ...(remainingBusinesses ? {} : {}) }
          : s
      ));
      toast.success('Estado actualizado');
      await fetchSalesReports(); // refresh to get client_id from backend
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const saleData = {
      ...formData,
      id: editingSale?.id || crypto.randomUUID(),
      advisor_id: user?.id,
      advisor_name: user?.full_name || user?.email,
      created_at: editingSale?.created_at || new Date().toISOString()
    };

    try {
      if (editingSale) {
        await axios.put(`${API}/sales-reports/${editingSale.id}`, saleData);
        setSalesReports(prev => prev.map(s => s.id === editingSale.id ? { ...s, ...saleData } : s));
        toast.success('Registro actualizado');
      } else {
        await axios.post(`${API}/sales-reports`, saleData);
        toast.success('Registro creado');
        // Reload from backend so the record with its report_month appears in the right tab
        await fetchSalesReports();
        if (saleData.report_month) {
          setActiveMonth(saleData.report_month);
        }
      }
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar el registro');
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      await axios.delete(`${API}/sales-reports/${id}`).catch(() => {});
      setSalesReports(prev => prev.filter(s => s.id !== id));
      toast.success('Registro eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Handle edit
  const handleEdit = (sale) => {
    setEditingSale(sale);
    setFormData({
      client_name: sale.client_name || '',
      occupation: sale.occupation || '',
      business_type: sale.business_type || '',
      amount: sale.amount || '',
      periodicity: sale.periodicity || 'Mensual',
      term: sale.term || '',
      next_contact_date: sale.next_contact_date || '',
      status: sale.status || 'nuevo',
      presentation: sale.presentation || false,
      comments: sale.comments || ''
    });
    setDialogOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setEditingSale(null);
    setFormData({
      client_name: '',
      occupation: '',
      business_type: '',
      amount: '',
      periodicity: 'Mensual',
      term: '',
      next_contact_date: '',
      status: 'nuevo',
      presentation: false,
      comments: '',
      report_month: currentMonthLabel,
    });
  };

  // Get visible columns
  const visibleColumns = columns.filter(col => col.visible);

  // Month tabs: unique sorted months from salesReports (most recent first)
  const availableMonths = React.useMemo(() => {
    const months = [...new Set(salesReports.map(s => s.report_month).filter(Boolean))];
    months.sort((a, b) => {
      // Sort by extracting year+month index
      const parse = (m) => {
        const parts = m.split(' ');
        const yr = parseInt(parts[parts.length - 1]) || 0;
        const monthIdx = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre']
          .findIndex(mn => m.toLowerCase().includes(mn));
        return yr * 12 + (monthIdx >= 0 ? monthIdx : 0);
      };
      return parse(b) - parse(a); // descending (most recent first)
    });
    return months;
  }, [salesReports]);

  // Auto-select most recent month when months load
  React.useEffect(() => {
    if (availableMonths.length > 0 && (activeMonth === null || !availableMonths.includes(activeMonth))) {
      setActiveMonth(availableMonths[0]);
    }
  }, [availableMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredReports = activeMonth
    ? salesReports.filter(s => s.report_month === activeMonth)
    : salesReports;

  // Get status label
  const getStatusLabel = (status) => {
    return statusOptions.find(s => s.value === status)?.label || status;
  };

  return (
    <div className="space-y-4" data-testid="sales-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">Reporte de Ventas</h2>
            <p className="text-sm text-muted-foreground">Registro de prospectos y ventas</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setColumnsDialogOpen(true)}>
            <Settings2 className="h-4 w-4 mr-2" />
            Columnas
          </Button>
          <ImportExportManager
            module="sales-reports"
            apiUrl={API}
            data={salesReports}
            onSuccess={fetchSalesReports}
            currentUserName={user?.full_name || user?.email}
          />
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1a365d] hover:bg-[#1a365d]/90">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Registro
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSale ? 'Editar Registro' : 'Nuevo Registro de Venta'}</DialogTitle>
                <DialogDescription>
                  {editingSale ? 'Modifica los datos del registro' : 'Agrega un nuevo prospecto o venta'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="client_name">Nombre del Cliente *</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="Nombre completo"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="occupation">Ocupación</Label>
                    <Input
                      id="occupation"
                      value={formData.occupation}
                      onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
                      placeholder="Ej: Empresario"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_type">Negocio</Label>
                    <Input
                      id="business_type"
                      value={formData.business_type}
                      onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                      placeholder="Ej: OptiMaxx Plus, Ahorro"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Monto (MXN)</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="50000"
                      min="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="periodicity">Periodicidad</Label>
                    <Select
                      value={formData.periodicity}
                      onValueChange={(v) => setFormData({ ...formData, periodicity: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {periodicityOptions.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="term">Plazo</Label>
                    <Input
                      id="term"
                      value={formData.term}
                      onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                      placeholder="Ej: 25 años"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="next_contact_date">Fecha de próximo contacto</Label>
                    <Input
                      id="next_contact_date"
                      value={formData.next_contact_date}
                      onChange={(e) => setFormData({ ...formData, next_contact_date: e.target.value })}
                      placeholder="Ej: 11 de febrero 2026"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="status">Estado</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => setFormData({ ...formData, status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Presentación</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Switch
                        checked={formData.presentation}
                        onCheckedChange={(checked) => setFormData({ ...formData, presentation: checked })}
                      />
                      <span className="text-sm">{formData.presentation ? 'Sí' : 'No'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="report_month">Mes del Reporte</Label>
                  <Input
                    id="report_month"
                    value={formData.report_month}
                    onChange={(e) => setFormData({ ...formData, report_month: e.target.value })}
                    placeholder="Ej: Marzo 2026"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comments">Comentarios (opcional)</Label>
                  <Textarea
                    id="comments"
                    value={formData.comments}
                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                    placeholder="Notas adicionales..."
                    rows={3}
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#1a365d] hover:bg-[#1a365d]/90">
                    {editingSale ? 'Guardar Cambios' : 'Crear Registro'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Month tabs */}
      {availableMonths.length > 0 && (
        <div className="flex gap-1 flex-wrap border-b">
          {availableMonths.map(month => (
            <button
              key={month}
              onClick={() => setActiveMonth(month)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeMonth === month
                  ? 'border-[#1a365d] text-[#1a365d] dark:text-blue-300'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              {month}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map(col => (
                    <TableHead key={col.id}>{col.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : filteredReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="text-center py-8 text-muted-foreground">
                      <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      No hay registros para este mes. Importa un archivo Excel o crea uno nuevo.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReports.map((sale) => (
                    <TableRow key={sale.id}>
                      {visibleColumns.map(col => (
                        <TableCell key={col.id}>
                          {col.id === 'client_name' && (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium">{sale.client_name}</span>
                              {sale.client_id && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${CONVERSION_BADGE}`}>
                                  Convertido
                                </span>
                              )}
                            </div>
                          )}
                          {col.id === 'occupation' && (sale.occupation || '-')}
                          {col.id === 'business_type' && (
                            sale.business_type ? (() => {
                              const types = String(sale.business_type).split(',').map(s => s.trim()).filter(Boolean);
                              const visible = types.slice(0, 2);
                              const extra = types.length - 2;
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {visible.map((bt, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{bt}</Badge>
                                  ))}
                                  {extra > 0 && (
                                    <Badge variant="outline" className="text-xs text-muted-foreground">+{extra}</Badge>
                                  )}
                                </div>
                              );
                            })() : '-'
                          )}
                          {col.id === 'advisor_name' && (sale.advisor_name || '-')}
                          {col.id === 'amount' && (
                            <span className="font-medium text-green-600">
                              {sale.amount ? formatCurrency(sale.amount) : '-'}
                            </span>
                          )}
                          {col.id === 'periodicity' && (
                            sale.periodicity ? (
                              <Badge className={PERIODICITY_COLOR}>{sale.periodicity}</Badge>
                            ) : '-'
                          )}
                          {col.id === 'term' && (sale.term || '-')}
                          {col.id === 'next_contact_date' && (sale.next_contact_date || '-')}
                          {col.id === 'status' && (
                            <Select
                              value={sale.status || 'nuevo'}
                              onValueChange={(v) => handleStatusChange(sale.id, v)}
                            >
                              <SelectTrigger className={`w-[140px] h-8 text-xs ${STATUS_COLORS[sale.status] || STATUS_COLORS.nuevo}`}>
                                <SelectValue>{getStatusLabel(sale.status || 'nuevo')}</SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {col.id === 'presentation' && (
                            <Badge className={sale.presentation ? PRESENTATION_YES : PRESENTATION_NO}>
                              {sale.presentation ? 'Sí' : 'No'}
                            </Badge>
                          )}
                          {col.id === 'comments' && (
                            <CommentTooltip text={sale.comments} />
                          )}
                          {col.id === 'report_month' && (
                            <span className="text-sm text-muted-foreground">{sale.report_month || '-'}</span>
                          )}
                          {col.id === 'actions' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(sale)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(sale.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      {filteredReports.length > 0 && (
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>Total: {filteredReports.length} registros</span>
          <span>•</span>
          <span>Ganados: {filteredReports.filter(s => s.status === 'ganado').length}</span>
          <span>•</span>
          <span>En negociación: {filteredReports.filter(s => s.status === 'en_negociacion').length}</span>
        </div>
      )}

      {/* Column Customization Dialog */}
      <Dialog open={columnsDialogOpen} onOpenChange={setColumnsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Personalizar Columnas</DialogTitle>
            <DialogDescription>
              Arrastra para reordenar. Usa el switch para mostrar/ocultar.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={columns.map(c => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {columns.map(column => (
                    <SortableColumnItem
                      key={column.id}
                      column={column}
                      onToggle={toggleColumnVisibility}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setColumnsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveColumnPreferences} className="bg-[#1a365d] hover:bg-[#1a365d]/90">
              <Save className="h-4 w-4 mr-2" />
              Guardar Preferencias
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BusinessSelectionModal
        open={bizSelectOpen}
        onOpenChange={setBizSelectOpen}
        businessTypes={
          bizSelectSale?.business_type
            ? String(bizSelectSale.business_type).split(',').map(s => s.trim()).filter(Boolean)
            : []
        }
        onConfirm={(selected, remaining) => {
          if (bizSelectSale) {
            _applyStatusChange(bizSelectSale, pendingStatus, selected, remaining);
          }
          setBizSelectSale(null);
          setPendingStatus('');
        }}
      />

    </div>
  );
};

export default SalesReport;
